import React from 'react';
import PolicyPageLayout from '../components/PolicyPageLayout';

const PrivacyPolicy = () => {
  const sections = [
    {
      title: 'Our Commitment to Privacy',
      content: (
        <>
          <p>The Federal University Wukari Journal of Agriculture and Life Sciences (FUWJALS) is committed to protecting the privacy and confidentiality of our users' personal information. This privacy policy explains how we collect, use, and protect your information when you visit our website or use our services.</p>
          <p className="text-muted"><strong>Last Updated:</strong> September 15, 2024</p>
        </>
      )
    },
    {
      title: 'Information We Collect',
      content: (
        <>
          <h4>Personal Information</h4>
          <p>We collect personal information that you voluntarily provide to us, including:</p>
          <ul className="policy-section-list">
            <li><strong>Account Registration:</strong> Name, email address, institutional affiliation, ORCID ID</li>
            <li><strong>Manuscript Submission:</strong> Author details, contact information, biographical information</li>
            <li><strong>Peer Review:</strong> Reviewer information, expertise areas, availability</li>
            <li><strong>Communication:</strong> Email correspondence, feedback, inquiries</li>
            <li><strong>Payment Information:</strong> Billing details for publication fees (processed securely)</li>
          </ul>

          <h4>Technical Information</h4>
          <ul className="policy-section-list">
            <li>IP addresses and geographic location</li>
            <li>Browser type and version</li>
            <li>Operating system information</li>
            <li>Pages visited and time spent on site</li>
            <li>Referral sources</li>
            <li>Download and access patterns</li>
          </ul>
        </>
      )
    },
    {
      title: 'How We Use Your Information',
      content: 'We use the collected information for the following purposes:',
      list: [
        'Processing manuscript submissions and peer reviews',
        'Managing user accounts and authentication',
        'Communicating about submissions, reviews, and publications',
        'Processing publication fees and payments',
        'Improving our website functionality and user experience',
        'Generating usage statistics and analytics',
        'Preventing fraud and ensuring security',
        'Complying with legal obligations'
      ]
    },
    {
      title: 'Information Sharing and Disclosure',
      content: (
        <>
          <p>We respect your privacy and do not sell, rent, or trade your personal information. We may share information only in these circumstances:</p>
          <ul className="policy-section-list">
            <li><strong>With Consent:</strong> When you explicitly authorize us to share information</li>
            <li><strong>Service Providers:</strong> With trusted partners who assist in operating our website</li>
            <li><strong>Legal Requirements:</strong> When required by law or to protect rights and safety</li>
            <li><strong>Academic Purposes:</strong> Author names and affiliations in published articles</li>
            <li><strong>Institutional Partners:</strong> With Federal University Wukari for administrative purposes</li>
          </ul>
        </>
      )
    },
    {
      title: 'Data Security',
      content: (
        <>
          <p>We implement appropriate technical and organizational measures to protect your personal information:</p>
          <ul className="policy-section-list">
            <li>Secure Socket Layer (SSL) encryption for data transmission</li>
            <li>Regular security audits and updates</li>
            <li>Access controls and authentication systems</li>
            <li>Regular data backups and recovery procedures</li>
            <li>Staff training on data protection practices</li>
          </ul>
          <p>While we strive to protect your information, no method of transmission over the internet is 100% secure.</p>
        </>
      )
    },
    {
      title: 'Your Rights and Choices',
      content: 'You have the following rights regarding your personal information:',
      list: [
        'Access: Request copies of your personal data',
        'Correction: Request correction of inaccurate information',
        'Deletion: Request removal of your personal data',
        'Portability: Receive your data in a portable format',
        'Objection: Object to certain uses of your information',
        'Withdrawal: Withdraw consent for data processing'
      ]
    },
    {
      title: 'Cookies and Tracking',
      content: (
        <>
          <p>We use cookies and similar technologies to:</p>
          <ul className="policy-section-list">
            <li>Remember your preferences and settings</li>
            <li>Authenticate users and prevent fraud</li>
            <li>Analyze site traffic and usage patterns</li>
            <li>Improve website performance</li>
          </ul>
          <p>You can control cookies through your browser settings, but disabling them may affect site functionality.</p>
        </>
      )
    },
    {
      title: 'Data Retention',
      content: (
        <>
          <p>We retain personal information for as long as necessary to fulfill the purposes outlined in this policy:</p>
          <ul className="policy-section-list">
            <li><strong>Account Information:</strong> Retained while account is active</li>
            <li><strong>Submission Records:</strong> Permanently retained for academic integrity</li>
            <li><strong>Financial Records:</strong> Retained as required by law (typically 7 years)</li>
            <li><strong>Communications:</strong> Retained for 3 years or as legally required</li>
          </ul>
        </>
      )
    },
    {
      title: 'International Data Transfers',
      content: 'If you access our services from outside Nigeria, your information may be transferred to and processed in Nigeria. By using our services, you consent to such transfers.'
    },
    {
      title: 'Children\'s Privacy',
      content: 'Our services are not intended for individuals under 18 years of age. We do not knowingly collect personal information from children.'
    },
    {
      title: 'Changes to This Policy',
      content: 'We may update this privacy policy periodically. We will notify registered users of significant changes via email and post the updated policy on our website with the revision date.'
    },
    {
      title: 'Contact Information',
      content: 'For questions about this privacy policy or our data practices, please contact:',
      highlight: (
        <>
          <strong>Data Protection Officer</strong><br />
          FUWJALS Editorial Office<br />
          Federal University Wukari<br />
          Email: privacy@fuwjals.edu.ng<br />
          Phone: +234 XXX XXX XXXX
        </>
      )
    }
  ];

  return (
    <PolicyPageLayout
      title="Privacy Policy"
      sections={sections}
      icon="🔒"
    />
  );
};

export default PrivacyPolicy;